/**
 * Duenner Promise-Wrapper um IndexedDB.
 *
 * Bewusst handgeschrieben statt einer Library: die App soll zur Laufzeit
 * keinen fremden Code enthalten, und wir brauchen nur vier Operationen.
 */

const DB_NAME = 'leadsheet';
/**
 * Bei Schema-Aenderungen erhoehen. Die Anlage der Stores unten prueft jeweils,
 * ob es sie schon gibt, deshalb genuegt das Erhoehen.
 * 2: Store "notes" dazugekommen.
 * 3: Store "projects" dazugekommen.
 * 4: Stores "rooms" und "boards" fuer die Band-Bibel dazugekommen.
 *    Die Handschrift darauf liegt weiter im vorhandenen "drawings"-Store,
 *    es gibt bewusst keine zweite Datenbank.
 * 5: Store "releases" fuer Alben, EPs und Singles dazugekommen.
 */
const DB_VERSION = 5;

export const STORE = {
  projects: 'projects',
  rooms: 'rooms',
  boards: 'boards',
  releases: 'releases',
  songs: 'songs',
  setlists: 'setlists',
  drawings: 'drawings',
  notes: 'notes',
  settings: 'settings',
} as const;

export type StoreName = (typeof STORE)[keyof typeof STORE];

/**
 * Ein anderer Speicher mit denselben Operationen, z. B. die Cloud.
 *
 * Ist einer eingehaengt, gehen alle Zugriffe unten dorthin statt in die
 * IndexedDB. repo.ts und die Ansichten merken davon nichts.
 */
export interface RemoteStore {
  getAll<T>(store: StoreName): Promise<T[]>;
  get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined>;
  put<T>(store: StoreName, value: T): Promise<void>;
  putMany<T>(store: StoreName, values: T[]): Promise<void>;
  del(store: StoreName, key: IDBValidKey): Promise<void>;
  clear(stores: StoreName[]): Promise<void>;
}

let remote: RemoteStore | null = null;

/** Haengt einen anderen Speicher ein. Einmal beim Start, vor dem ersten Zugriff. */
export function setRemoteStore(next: RemoteStore | null): void {
  remote = next;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/** Oeffnet die Datenbank (einmalig) und legt fehlende Stores an. */
export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE.projects)) {
        const projects = db.createObjectStore(STORE.projects, { keyPath: 'id' });
        projects.createIndex('createdAt', 'createdAt');
      }
      if (!db.objectStoreNames.contains(STORE.rooms)) {
        const rooms = db.createObjectStore(STORE.rooms, { keyPath: 'id' });
        rooms.createIndex('projectId', 'projectId');
      }
      if (!db.objectStoreNames.contains(STORE.boards)) {
        const boards = db.createObjectStore(STORE.boards, { keyPath: 'id' });
        boards.createIndex('roomId', 'roomId');
      }
      if (!db.objectStoreNames.contains(STORE.releases)) {
        const releases = db.createObjectStore(STORE.releases, { keyPath: 'id' });
        releases.createIndex('projectId', 'projectId');
      }
      if (!db.objectStoreNames.contains(STORE.songs)) {
        const songs = db.createObjectStore(STORE.songs, { keyPath: 'id' });
        songs.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(STORE.setlists)) {
        const setlists = db.createObjectStore(STORE.setlists, { keyPath: 'id' });
        setlists.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(STORE.drawings)) {
        db.createObjectStore(STORE.drawings, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE.notes)) {
        const notes = db.createObjectStore(STORE.notes, { keyPath: 'id' });
        notes.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains(STORE.settings)) {
        db.createObjectStore(STORE.settings, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('Datenbank blockiert: bitte andere Tabs der App schliessen.'));
  });
  return dbPromise;
}

/** Wandelt einen IDBRequest in ein Promise. */
function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Fuehrt `fn` in einer Transaktion aus und wartet, bis sie abgeschlossen ist. */
export async function tx<T>(
  stores: StoreName | StoreName[],
  mode: IDBTransactionMode,
  fn: (t: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  const transaction = db.transaction(stores, mode);
  const result = await fn(transaction);
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error('Transaktion abgebrochen'));
  });
  return result;
}

export async function getAll<T>(store: StoreName): Promise<T[]> {
  if (remote) return remote.getAll<T>(store);
  return tx(store, 'readonly', (t) => wrap<T[]>(t.objectStore(store).getAll()));
}

export async function get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  if (remote) return remote.get<T>(store, key);
  return tx(store, 'readonly', (t) => wrap<T | undefined>(t.objectStore(store).get(key)));
}

export async function put<T>(store: StoreName, value: T): Promise<void> {
  if (remote) return remote.put(store, value);
  await tx(store, 'readwrite', (t) => wrap(t.objectStore(store).put(value)));
}

export async function putMany<T>(store: StoreName, values: T[]): Promise<void> {
  if (remote) return remote.putMany(store, values);
  await tx(store, 'readwrite', (t) => {
    const os = t.objectStore(store);
    for (const v of values) os.put(v);
  });
}

export async function del(store: StoreName, key: IDBValidKey): Promise<void> {
  if (remote) return remote.del(store, key);
  await tx(store, 'readwrite', (t) => wrap(t.objectStore(store).delete(key)));
}

export async function clear(stores: StoreName[]): Promise<void> {
  if (remote) return remote.clear(stores);
  await tx(stores, 'readwrite', (t) => {
    for (const s of stores) t.objectStore(s).clear();
  });
}
